import type { KpiMetrics } from "../../api";
import { fmtInt, fmtMoney, fmtQty } from "../../lib/format";
import type { ChartToken } from "../../lib/chart-colors";

export type KpiDescriptor = {
  key: keyof KpiMetrics;
  /** analytics.json → kpi.<labelKey> */
  labelKey: string;
  format: (v: string | number) => string;
  /** `styles.css` dagi grafik tokeni — hex EMAS (mavzuga moslashadi). */
  color: ChartToken;
  /** Kartadagi kichik ikkinchi qator (masalan miqdor yonidagi summa). */
  subKey?: keyof KpiMetrics;
  /** Tovar moduliga bog'liq — omborsiz tarifda (comfort) yashiriladi. */
  needsProducts?: boolean;
  /** Izoh matni kaliti — kpi.hints.<hintKey>. */
  hintKey?: string;
  /** O'sish yaxshimi? Qaytarishlarda `false` (o'sishi yomon xabar). */
  higherIsBetter?: boolean;
};

/**
 * KPI ro'yxati — kartalar HAM, jadval ustunlari HAM shu yagona ro'yxatdan
 * yasaladi. Ikki joyda qo'lda takrorlansa vaqt o'tib bir-biridan uzilib
 * qolardi (bir joyda yangi KPI qo'shilib, ikkinchisida esdan chiqadi).
 *
 * Tartib ATAYIN: avval pul o'lchovlari (sof tushum → umumiy → foyda →
 * o'rtacha chek → tovar), keyin sanoqlar (xizmat → qaytarish → yangi →
 * qaytgan). Kartalar to'ri shu tartibda to'ldirilgani uchun qatorlar
 * tasodifiy emas, mazmunli bo'linadi.
 */
export const KPI_DESCRIPTORS: KpiDescriptor[] = [
  {
    key: "net_revenue",
    labelKey: "netRevenue",
    format: fmtMoney,
    color: "chart-1",
    needsProducts: true,
    hintKey: "netRevenue",
  },
  { key: "total_revenue", labelKey: "totalRevenue", format: fmtMoney, color: "chart-1" },
  {
    key: "profit",
    labelKey: "profit",
    format: fmtMoney,
    color: "chart-positive",
    needsProducts: true,
    hintKey: "profit",
  },
  {
    key: "avg_transaction",
    labelKey: "avgTransaction",
    format: fmtMoney,
    color: "chart-3",
    hintKey: "avgTransaction",
  },
  {
    key: "products_qty",
    // Miqdor `Numeric(12,3)` — `fmtQty` kasrni saqlaydi (2,5 litr "3" bo'lib
    // qolmasligi kerak).
    labelKey: "productsSold",
    format: fmtQty,
    color: "chart-4",
    subKey: "products_revenue",
    needsProducts: true,
  },
  {
    key: "services_count",
    labelKey: "servicesSold",
    format: (v) => fmtInt(Number(v)),
    color: "chart-5",
    subKey: "services_revenue",
  },
  {
    key: "returns_value",
    labelKey: "returns",
    format: fmtMoney,
    color: "chart-negative",
    subKey: "returns_count",
    needsProducts: true,
    hintKey: "returns",
    // Qaytarishlarning o'sishi — yomon xabar.
    higherIsBetter: false,
  },
  {
    key: "new_clients",
    labelKey: "newClients",
    format: (v) => fmtInt(Number(v)),
    color: "chart-2",
    // Ustaxona-global: backend `new_clients` uchun filial filtrini ATAYIN
    // qo'llamaydi. Izoh grafik kartasida ham bor, lekin o'lchov KO'RSATILGAN
    // har joyda turishi kerak — aks holda filial tanlagan ega bu sonni
    // "shu filialning yangi mijozlari" deb o'qiydi.
    hintKey: "newClients",
  },
  {
    key: "returning_clients",
    labelKey: "returningClients",
    format: (v) => fmtInt(Number(v)),
    color: "chart-6",
  },
];

/** Jadval "Jami" qatorida ustun yig'indisi MA'NOSIZ bo'lgan o'lchovlar. */
export const NON_ADDITIVE: ReadonlySet<keyof KpiMetrics> = new Set([
  "avg_transaction", // nisbat
  "returning_clients", // distinct sanoq — bir mijoz ikki kunda ikki marta
]);

/** Bir sub qiymatni kartaning ikkinchi qatoriga formatlaydi. */
export function subLabel(d: KpiDescriptor, m: KpiMetrics): string | undefined {
  if (!d.subKey) return undefined;
  const v = m[d.subKey];
  // Miqdor ustunining sub'i — summa; summa ustunining sub'i — hujjat soni.
  return typeof v === "number" ? fmtInt(v) : fmtMoney(v);
}
